import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

/** Açık ekran sekmesi (Delphi MDI child form karşılığı) */
export interface Tab {
  id: string;
  title: string;
  /** screen: menü TAG ekranı · report-group: rapor grubu listesi · report: tek rapor · custom: özel bileşen (belge vb.) */
  kind: 'screen' | 'report-group' | 'report' | 'custom';
  tag?: number;
  reportGroup?: string;
  reportId?: number;
  component?: string;
  props?: Record<string, any>;
}

interface WS {
  tabs: Tab[];
  active: string | null;
  open: (t: Omit<Tab, 'id'> & { id?: string }, opts?: { newInstance?: boolean }) => void;
  close: (id: string) => void;
  /** null: ana sayfa (gösterge paneli) */
  activate: (id: string | null) => void;
  rename: (id: string, title: string) => void;
}
const Ctx = createContext<WS | null>(null);

export function WorkspaceProvider({ children, storageKey, singleInstance }: { children: ReactNode; storageKey: string; singleInstance: boolean }) {
  const [tabs, setTabs] = useState<Tab[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? '[]');
    } catch {
      return [];
    }
  });
  // oturum açılışında ana sayfa gösterilir; önceki sekmeler yerinde durur
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(tabs.filter((t) => t.kind !== 'custom' || t.props?.persist)));
    } catch { /* */ }
  }, [tabs, storageKey]);

  const open: WS['open'] = useCallback((t, opts) => {
    const key = t.id ?? `${t.kind}:${t.tag ?? ''}:${t.reportGroup ?? ''}:${t.reportId ?? ''}:${t.component ?? ''}:${JSON.stringify(t.props ?? {})}`;
    setTabs((prev) => {
      // Delphi util2.Ekran(): kullanıcı "Ekran" yetkisinde aynı başlıklı form tekrar açılmaz, öne getirilir
      const existing = prev.find((x) => x.id === key);
      if (existing && (singleInstance || !opts?.newInstance)) {
        setActive(existing.id);
        return prev;
      }
      const id = existing ? `${key}#${Date.now()}` : key;
      setActive(id);
      return [...prev, { ...t, id } as Tab];
    });
  }, [singleInstance]);

  const close = useCallback((id: string) => {
    setTabs((prev) => {
      const i = prev.findIndex((x) => x.id === id);
      const next = prev.filter((x) => x.id !== id);
      setActive((a) => (a === id ? next[Math.min(i, next.length - 1)]?.id ?? null : a));
      return next;
    });
  }, []);

  const rename = useCallback((id: string, title: string) => setTabs((p) => p.map((t) => (t.id === id ? { ...t, title } : t))), []);

  return <Ctx.Provider value={{ tabs, active, open, close, activate: setActive, rename }}>{children}</Ctx.Provider>;
}

export function useWorkspace() {
  const c = useContext(Ctx);
  if (!c) throw new Error('WorkspaceProvider eksik');
  return c;
}

/** Sekmenin kendi kimliği (ekran içinden başlık değiştirme / kapatma için) */
export const TabIdContext = createContext<string>('');
export const useTabId = () => useContext(TabIdContext);
